import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MdErrorOutline, MdLockOutline, MdMailOutline } from "react-icons/md";
import { conferirLink, definirSenha } from "../../services/authApi";
import { saveSession } from "../../services/session";
import { primePermissions } from "../../auth/PermissionProvider";
import { homeRouteFor } from "../../utils/homeRoute";
import { guardarVoltar, voltarDaBusca } from "../../auth/voltar";
import { ConferirLinkResponse, DefinirSenhaErro } from "../../interfaces/Auth";
import { MENSAGEM_MUITAS_TENTATIVAS } from "./loginError";
import { guardarLinkConvite, iniciarLogin } from "../../services/entra";

const inputCls =
  "w-full rounded-xl border border-gray-200 bg-white px-3 py-3 text-sm text-gray-800 transition-all focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 dark:border-white/10 dark:bg-navy-900 dark:text-white";

export const TAMANHO_MINIMO_SENHA = 10;
const MENSAGEM_LINK_INVALIDO = "Este link expirou ou já foi usado. Peça um novo.";

type Resposta = { status?: number; data?: unknown };
const respostaDe = (e: unknown) => (e as { response?: Resposta } | null | undefined)?.response;

/** Mensagem do 400/403 do backend (`{ message }`); 429 e falhas sem corpo têm frase própria. */
export function mensagemDoLink(error: unknown, padrao: string): string {
  const resp = respostaDe(error);
  const message = (resp?.data as { message?: unknown } | undefined)?.message;
  if (typeof message === "string" && message.trim()) return message;
  return resp?.status === 429 ? MENSAGEM_MUITAS_TENTATIVAS : padrao;
}

/** Linhas por regra de senha violada, quando o backend as manda em `erros`. */
export function errosDeSenha(error: unknown): string[] {
  const erros = (respostaDe(error)?.data as Partial<DefinirSenhaErro> | undefined)?.erros;
  return Array.isArray(erros) ? erros.filter((x): x is string => typeof x === "string") : [];
}

type Estado =
  | { tipo: "conferindo" }
  | { tipo: "invalido"; mensagem: string }
  | { tipo: "ok"; link: ConferirLinkResponse };

/**
 * Convite e redefinição caem aqui: `/auth/definir-senha?token=`. O token sai da URL assim que é
 * lido (não fica em histórico, referer nem captura de tela) e vive só em memória.
 */
const DefinirSenha: React.FC = () => {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const [token] = useState(() => new URLSearchParams(search).get("token") ?? "");
  const [voltar] = useState(() => voltarDaBusca(search));
  const [microsoft] = useState(() => new URLSearchParams(search).get("metodo") === "microsoft");
  const [estado, setEstado] = useState<Estado>({ tipo: "conferindo" });
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmacao, setConfirmacao] = useState("");
  const [erro, setErro] = useState("");
  const [detalhes, setDetalhes] = useState<string[]>([]);
  const [enviando, setEnviando] = useState(false);

  useEffect(() => {
    window.history.replaceState(window.history.state, "", pathname);
  }, [pathname]);

  useEffect(() => {
    let vivo = true;
    if (!token) {
      setEstado({ tipo: "invalido", mensagem: MENSAGEM_LINK_INVALIDO });
      return;
    }
    conferirLink(token)
      .then((link) => vivo && setEstado({ tipo: "ok", link }))
      .catch((e) => vivo && setEstado({ tipo: "invalido", mensagem: mensagemDoLink(e, MENSAGEM_LINK_INVALIDO) }));
    return () => {
      vivo = false;
    };
  }, [token]);

  const enviar = async (e: React.FormEvent) => {
    e.preventDefault();
    setDetalhes([]);
    if (novaSenha.length < TAMANHO_MINIMO_SENHA) {
      setErro(`A senha precisa ter pelo menos ${TAMANHO_MINIMO_SENHA} caracteres.`);
      return;
    }
    if (novaSenha !== confirmacao) {
      setErro("A confirmação não é igual à nova senha.");
      return;
    }
    setErro("");
    setEnviando(true);
    try {
      const sessao = await definirSenha(token, novaSenha);
      saveSession(sessao.jwtToken, sessao.refreshToken);
      localStorage.setItem("userEmail", sessao.email);
      const home = homeRouteFor(await primePermissions(sessao.jwtToken));
      navigate(voltar ?? home, { replace: true });
    } catch (err) {
      setErro(mensagemDoLink(err, "Não foi possível definir a senha. Tente de novo em instantes."));
      setDetalhes(errosDeSenha(err));
      setEnviando(false);
    }
  };

  const entrarComMicrosoft = async (email: string) => {
    setErro("");
    setEnviando(true);
    guardarLinkConvite(token);
    // A volta da Microsoft cai em /auth/sign-in sem a query: o destino vai junto no storage.
    guardarVoltar(voltar);
    try {
      await iniciarLogin(email); // sai da página
    } catch {
      setEnviando(false);
      setErro("Não foi possível abrir o login da Microsoft.");
    }
  };

  if (estado.tipo === "conferindo") {
    return (
      <div className="flex min-h-dvh w-full items-center justify-center px-6 py-10">
        <p role="status" className="text-sm font-medium text-gray-600 dark:text-gray-300">
          Conferindo seu link…
        </p>
      </div>
    );
  }

  if (estado.tipo === "invalido") {
    return (
      <div className="flex min-h-dvh w-full items-center justify-center px-6 py-10">
        <main className="animate-fade-up w-full max-w-sm">
          <h1 className="text-2xl font-extrabold tracking-tight text-navy-800 dark:text-white">
            Link inválido
          </h1>
          <div
            role="alert"
            className="mt-6 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300"
          >
            <MdErrorOutline className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{estado.mensagem}</span>
          </div>
          <Link
            to="/auth/esqueci"
            className="mt-7 flex w-full items-center justify-center rounded-xl bg-navy-800 px-5 py-3 text-sm font-bold text-white shadow-md transition-all hover:bg-navy-900 active:scale-[.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:bg-white dark:text-navy-900 dark:hover:bg-gray-100"
          >
            Pedir novo link
          </Link>
        </main>
      </div>
    );
  }

  const { link } = estado;
  const convite = link.finalidade === "Convite";
  const lock = (
    <MdLockOutline
      className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
      aria-hidden="true"
    />
  );

  if (microsoft) {
    return (
      <div className="flex min-h-dvh w-full items-center justify-center px-6 py-10">
        <main className="animate-fade-up w-full max-w-sm">
          <h1 className="text-2xl font-extrabold tracking-tight text-navy-800 dark:text-white">
            Entrar com a Microsoft
          </h1>
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
            {link.organizacao
              ? `Você foi convidado para o NINA da ${link.organizacao}. Entre com a conta Microsoft do seu e-mail.`
              : "Entre com a conta Microsoft do seu e-mail para acessar o NINA."}
          </p>
          {erro && (
            <div
              role="alert"
              className="mt-6 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300"
            >
              <MdErrorOutline className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{erro}</span>
            </div>
          )}
          <div className="mt-6 flex flex-col gap-1.5">
            <label htmlFor="email" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              E-mail
            </label>
            <div className="relative">
              <MdMailOutline
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input id="email" type="email" value={link.email} readOnly className={`${inputCls} pl-9 opacity-80`} />
            </div>
          </div>
          <button
            type="button"
            onClick={() => entrarComMicrosoft(link.email)}
            disabled={enviando}
            className="mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-navy-800 px-5 py-3 text-sm font-bold text-white shadow-md transition-all hover:bg-navy-900 active:scale-[.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-navy-900 dark:hover:bg-gray-100"
          >
            {enviando ? "Conectando…" : "Entrar com sua conta Microsoft"}
          </button>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh w-full items-center justify-center px-6 py-10">
      <main className="animate-fade-up w-full max-w-sm">
        <h1 className="text-2xl font-extrabold tracking-tight text-navy-800 dark:text-white">
          {convite ? "Criar minha senha" : "Criar nova senha"}
        </h1>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
          {convite && link.organizacao
            ? `Você foi convidado para o NINA da ${link.organizacao}. Defina uma senha para entrar.`
            : "Defina uma senha para entrar no NINA."}
        </p>

        <form onSubmit={enviar} className="mt-6">
          {erro && (
            <div
              role="alert"
              className="mb-2 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5 text-sm font-medium text-red-700 dark:border-red-500/30 dark:bg-red-900/20 dark:text-red-300"
            >
              <MdErrorOutline className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <div>
                <span>{erro}</span>
                {detalhes.length > 0 && (
                  <ul className="mt-1 list-disc pl-4 font-normal">
                    {detalhes.map((d) => (
                      <li key={d}>{d}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          <div className="mt-4 flex flex-col gap-1.5">
            <label htmlFor="email" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              E-mail
            </label>
            <div className="relative">
              <MdMailOutline
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                aria-hidden="true"
              />
              <input
                id="email"
                type="email"
                value={link.email}
                readOnly
                className={`${inputCls} pl-9 opacity-80`}
              />
            </div>
          </div>

          <div className="mt-4 flex flex-col gap-1.5">
            <label htmlFor="novaSenha" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Nova senha
            </label>
            <div className="relative">
              {lock}
              <input
                id="novaSenha"
                type="password"
                autoComplete="new-password"
                value={novaSenha}
                onChange={(ev) => setNovaSenha(ev.target.value)}
                aria-describedby="regraSenha"
                className={`${inputCls} pl-9`}
                required
              />
            </div>
            <p id="regraSenha" className="text-xs text-gray-500 dark:text-gray-400">
              Mínimo de {TAMANHO_MINIMO_SENHA} caracteres.
            </p>
          </div>

          <div className="mt-4 flex flex-col gap-1.5">
            <label htmlFor="confirmacao" className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Confirmar nova senha
            </label>
            <div className="relative">
              {lock}
              <input
                id="confirmacao"
                type="password"
                autoComplete="new-password"
                value={confirmacao}
                onChange={(ev) => setConfirmacao(ev.target.value)}
                className={`${inputCls} pl-9`}
                required
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={enviando}
            className="mt-7 flex w-full items-center justify-center gap-2 rounded-xl bg-navy-800 px-5 py-3 text-sm font-bold text-white shadow-md transition-all hover:bg-navy-900 active:scale-[.98] disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white dark:text-navy-900 dark:hover:bg-gray-100"
          >
            {enviando ? "Salvando…" : convite ? "Criar senha e entrar" : "Salvar e entrar"}
          </button>
        </form>
      </main>
    </div>
  );
};

export default DefinirSenha;
